"use strict";

class Hana_IG_Fonts_Widget {
    /** @type {HTMLElement} */
    #$widget;

    /** @type {HTMLTextAreaElement} */
    #$inputField;

    /** @type {HTMLButtonElement} */
    #$clearButton;

    /** @type {HTMLElement} */
    #$outputs;

    /** @type {HTMLTemplateElement} */
    #$outputItemTemplate;

    /**
     * Map of font names to output inputs.
     * @type {object}
     */
    #outputInputs;


    /**
     * Creates an instance of the widget.
     */
    constructor( $widget ) {
        this.#$widget             = $widget;
        this.#$inputField         = $widget.querySelector( ".hana-ig-fonts-input" );
        this.#$clearButton        = $widget.querySelector( ".hana-ig-fonts-clear" );
        this.#$outputs            = $widget.querySelector( ".hana-ig-fonts-output" );
        this.#$outputItemTemplate = $widget.querySelector( ".hana-ig-fonts-output-item-template" );

        this.#buildOutputItems();

        window.addEventListener( "load", this.refresh.bind( this ) );
        this.#$inputField.addEventListener( "input", this.refresh.bind( this ) );
        this.#$inputField.addEventListener( "click", this.onInputFieldClick.bind( this ) );
        this.#$clearButton.addEventListener( "click", this.onClearButtonClick.bind( this ) );
    }

    /**
     * Builds items for generated fonts.
     */
    #buildOutputItems() {
        this.#outputInputs = {};

        for ( const fontName in Hana_IG_Fonts_Chars ) {
            // Create item.
            const $item = document.createElement( "li" );
            $item.classList.add( "hana-ig-fonts-output-item" );
            $item.setAttribute( "data-font", fontName );
            $item.innerHTML = this.#$outputItemTemplate.innerHTML;

            // Assign event handlers.
            const $copyButton = $item.querySelector( ".hana-ig-fonts-copy" );
            $copyButton.addEventListener( "click", this.onCopyButtonClick.bind( this ) );

            // Append item to the output.
            this.#$outputs.appendChild( $item );

            // Store input.
            this.#outputInputs[ fontName ] = $item.querySelector( ".hana-ig-fonts-output-value" );
        }
    }

    /**
     * Refreshes the widget.
     */
    refresh() {
        this.#resizeTextarea( this.#$inputField );
        this.#regenerateFonts();
    }

    /**
     * Returns text in input field.
     * @returns {string}
     */
    getInput( usePlaceholderIfEmpty = true ) {
        const input = this.#$inputField.value.trim();

        if ( input.length > 0 ) {
            return input;
        } else if ( usePlaceholderIfEmpty ) {
            return this.#$inputField.placeholder;
        } else {
            return "";
        }
    }

    /**
     * Sets text in input field.
     * @param {string} value
     */
    setInput( value ) {
        this.#$inputField.value = value.trim();
        this.refresh();
    }

    /**
     * Called when input field has been focused.
     */
    onInputFieldClick() {
        if ( window.screen.availWidth >= 1200 ) {
            return;
        }

        setTimeout( () => {
            const inputFieldPosition = this.#$inputField.getBoundingClientRect().top;

            window.scrollTo( {
                top: ( window.scrollY + inputFieldPosition - 16 ),
                behavior: "smooth"
            } );
        }, 500 );
    }

    /**
     * Called when Clear button has been clicked.
     */
    onClearButtonClick() {
        if ( this.getInput( false ).length === 0 ) {
            return;
        }

        this.#$widget.classList.add( "-hidden" );

        setTimeout( () => {
            this.setInput( "" );
            this.#$widget.classList.remove( "-hidden" );
        }, 150 );
    }

    /**
     * Called when Copy button has been clicked.
     * @param {MouseEvent} event
     */
    onCopyButtonClick( event ) {
        const $button        = event.target;
        const $outputItem    = $button.closest( ".hana-ig-fonts-output-item" );
        const $outputField   = $outputItem.querySelector( ".hana-ig-fonts-output-value" );
        const $copiedTooltip = $outputItem.querySelector( ".hana-ig-fonts-copied" );

        // Copy value to clipboard.
        const value = $outputField.value.trim();

        if ( value.length > 0 ) {
            // noinspection JSIgnoredPromiseFromCall
            navigator.clipboard.writeText( value );
        }

        // Hide already visible tooltips.
        const $activeTooltips = this.#$outputs.querySelectorAll( ".hana-ig-fonts-copied.-active" );

        for ( const $tooltip of $activeTooltips ) {
            $tooltip.classList.remove( "-active" );
        }

        // Show tooltip.
        $copiedTooltip.classList.add( "-active" );

        setTimeout( () => {
            $copiedTooltip.classList.remove( "-active" );
        }, 1250 )
    }

    /**
     * Resizes textarea to fit its content.
     * @param {HTMLTextAreaElement} $textarea
     */
    #resizeTextarea( $textarea ) {
        $textarea.style.height = "1px";
        $textarea.style.height = ( $textarea.scrollHeight > 65 )
            ? $textarea.scrollHeight + "px"
            : "auto";
    }

    /**
     * Regenerates fonts according to the input.
     */
    #regenerateFonts() {
        // Get input value.
        const inputText = this.getInput();

        // Regenerate fonts.
        for ( const fontName in Hana_IG_Fonts_Chars ) {
            const $outputField = this.#outputInputs[ fontName ];
            $outputField.value = this.#generateFont( inputText, fontName );
            this.#resizeTextarea( $outputField );
        }
    }

    /**
     * Generates font from an input text.
     * @param {string} inputText
     * @param {string} fontName
     * @returns {string}
     */
    #generateFont( inputText, fontName ) {
        // Get replace maps in array form (strings will be comverted by spread operator).
        const font                 = Hana_IG_Fonts_Chars[ fontName ];
        const fontOriginal         = ( typeof font.original === "string" )
            ? [...font.original]
            : font.original;
        const fontReplace          = ( typeof font.replace === "string" )
            ? [...font.replace]
            : font.replace;
        const spacesMultiplyFactor = ( typeof font.spacesMultiplyFactor === "number" )
            ? font.spacesMultiplyFactor
            : 1;

        // Replace characters.
        let outputText = inputText;

        for ( let i = 0; i < fontOriginal.length; i++ ) {
            outputText = outputText.replaceAll( fontOriginal[ i ], fontReplace[ i ] );
        }

        // Multiply spaces.
        if ( spacesMultiplyFactor > 1 ) {
            outputText = outputText.replaceAll( " ", " ".repeat( spacesMultiplyFactor ) );
        }

        return outputText;
    }
}

// Initialize widgets.
window.addEventListener( "DOMContentLoaded", function() {
    const $widgets = document.querySelectorAll( ".hana-ig-fonts" );

    for ( const $widget of $widgets ) {
        $widget.igFonts = new Hana_IG_Fonts_Widget( $widget );
    }
} );
